import React, { useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Link2, Quote, Image as ImageIcon } from "lucide-react";

export const RichTextEditor = ({ value, onChange, onImageUpload }) => {
  const ref = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (ref.current) {
      const clean = DOMPurify.sanitize(value || "");
      if (ref.current.innerHTML !== clean) ref.current.innerHTML = clean;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => { if (ref.current) onChange(ref.current.innerHTML); };
  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };
  const addLink = () => {
    const url = window.prompt("Link URL (https://…)");
    if (url) exec("createLink", url);
  };
  const insertImg = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const url = await onImageUpload(f);
      ref.current?.focus();
      document.execCommand("insertHTML", false, `<img src="${url}" alt="" />`);
      emit();
    } catch (err) {
      /* handled upstream by toast */
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const Btn = ({ onClick, title, children, testid }) => (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} title={title}
      className="w-9 h-9 grid place-items-center rounded-lg text-slate-700 hover:bg-warm-surface hover:text-maroon transition-colors" data-testid={testid}>
      {children}
    </button>
  );

  return (
    <div className="border-2 border-amber-900/15 rounded-lg bg-white overflow-hidden" data-testid="blog-rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-amber-900/10 bg-ivory px-2 py-1.5 sticky top-0 z-10">
        <Btn onClick={() => exec("bold")} title="Bold" testid="rte-bold"><Bold size={16} /></Btn>
        <Btn onClick={() => exec("italic")} title="Italic" testid="rte-italic"><Italic size={16} /></Btn>
        <Btn onClick={() => exec("underline")} title="Underline" testid="rte-underline"><Underline size={16} /></Btn>
        <span className="w-px h-6 bg-amber-900/10 mx-1" />
        <Btn onClick={() => exec("formatBlock", "<h2>")} title="Heading" testid="rte-h2"><Heading2 size={16} /></Btn>
        <Btn onClick={() => exec("formatBlock", "<h3>")} title="Subheading" testid="rte-h3"><Heading3 size={16} /></Btn>
        <Btn onClick={() => exec("formatBlock", "<blockquote>")} title="Quote" testid="rte-quote"><Quote size={16} /></Btn>
        <Btn onClick={() => exec("formatBlock", "<p>")} title="Normal text" testid="rte-p"><span className="text-xs font-bold">P</span></Btn>
        <span className="w-px h-6 bg-amber-900/10 mx-1" />
        <Btn onClick={() => exec("insertUnorderedList")} title="Bulleted list" testid="rte-ul"><List size={16} /></Btn>
        <Btn onClick={() => exec("insertOrderedList")} title="Numbered list" testid="rte-ol"><ListOrdered size={16} /></Btn>
        <span className="w-px h-6 bg-amber-900/10 mx-1" />
        <Btn onClick={addLink} title="Insert link" testid="rte-link"><Link2 size={16} /></Btn>
        <label className="w-9 h-9 grid place-items-center rounded-lg text-slate-700 hover:bg-warm-surface hover:text-maroon transition-colors cursor-pointer" title="Insert image" data-testid="rte-image">
          <ImageIcon size={16} />
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={insertImg} />
        </label>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning onInput={emit}
        className="blog-content min-h-[320px] max-h-[520px] overflow-y-auto px-4 py-3 focus:outline-none text-slate-800"
        data-testid="blog-body-input" />
    </div>
  );
};
